import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/** Foto pequeña junto al nombre (Eduardo 2026-09-10): círculo con la foto
 * de perfil o de la credencial; con clic abre un popup con la foto grande.
 * Sin foto muestra las iniciales y no abre nada. No cambia el formato de
 * las tablas: vive dentro de la celda del nombre. */

export function playerInitials(name?: string | null): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "J";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
}

type Props = {
  name: string;
  photoUrl?: string | null;
  /** Texto secundario del popup (categoría, grupo…). */
  subtitle?: string | null;
  size?: "sm" | "md";
};

export default function PlayerPhotoChip({ name, photoUrl, subtitle, size = "sm" }: Props) {
  const [open, setOpen] = useState(false);
  // La foto de inscripción llega con liga firmada que vence en una hora (y
  // con loading="lazy" puede pedirse mucho después de cargar la pantalla): si
  // no carga, se muestran las iniciales en vez de una imagen rota.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const className = `player-photo-chip player-photo-chip--${size}`;

  if (!photoUrl || failedUrl === photoUrl) {
    return (
      <span className={`${className} player-photo-chip--initials`} aria-hidden="true">
        {playerInitials(name)}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`${className} player-photo-chip--photo`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        aria-label={`Ver foto de ${name}`}
        title={`Ver foto de ${name}`}
      >
        <img src={photoUrl} alt="" loading="lazy" onError={() => setFailedUrl(photoUrl)} />
      </button>
      {open
        ? createPortal(
            <div
              className="player-photo-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label={`Foto de ${name}`}
              onClick={() => setOpen(false)}
            >
              <div className="player-photo-lightbox__card" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  className="player-photo-lightbox__close"
                  onClick={() => setOpen(false)}
                  aria-label="Cerrar"
                  autoFocus
                >
                  ×
                </button>
                <img className="player-photo-lightbox__img" src={photoUrl} alt={`Foto de ${name}`} />
                <strong className="player-photo-lightbox__name">{name}</strong>
                {subtitle ? <span className="player-photo-lightbox__subtitle">{subtitle}</span> : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
